import { useFormikContext } from 'formik';
import { Dispatch, memo, SetStateAction, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';

import {
    AirportConfigurationType,
    GetGetConfigurationsResponse,
    KeyGetGetConfigurationsByType,
    useMutationDeleteDeleteConfigurations,
} from '../../../backend';
import { queryClient } from '../../../Root';
import { errorToastConfig } from '../../../utils/utils';
import { useConfirmDialog } from '../../confirm/useConfirmDialog';
import { ConfigurationsPageFormValues } from '../../pages/ConfigurationPage';
import { StyledFormBox, StyledFormButton } from './MultiConfigurationsForm.style';

export const MultiConfigurationsForm = memo(function MultiConfigurationsForm({
    selectedConfigurations,
    setSelectedConfigurations,
    arrayRemove,
    setAnchorEl,
    configurationType,
}: {
    selectedConfigurations: number[];
    setSelectedConfigurations: (e: number[]) => void;
    arrayRemove: (index: number) => void;
    setAnchorEl: Dispatch<SetStateAction<HTMLLabelElement>>;
    configurationType: AirportConfigurationType;
}) {
    const { values, dirty } = useFormikContext<ConfigurationsPageFormValues>();

    const confirm = useConfirmDialog({ disableReject: true });
    const { mutate } = useMutationDeleteDeleteConfigurations({
        onError: () => {
            toast(<FormattedMessage id={'configurationsForm.error.deleteConfiguration'} />, errorToastConfig);
        },
        onSuccess: (r, requestArgs) => {
            const deletedConfigurationUuids = requestArgs.airportConfiguration.map(r => r.uuid);
            queryClient.setQueriesData(
                {
                    queryKey: [
                        KeyGetGetConfigurationsByType,
                        {
                            type: configurationType,
                            includeNonActive: true,
                        },
                    ],
                },
                (values: GetGetConfigurationsResponse) => ({
                    ...values,
                    data: values.data.filter(r => !deletedConfigurationUuids.includes(r.uuid)),
                }),
            );
            setSelectedConfigurations([]);
        },
    });

    const handleDelete = useCallback(() => {
        setAnchorEl(null);
        const configurations = values.configurations.filter(r =>
            selectedConfigurations.includes(values.configurations.indexOf(r)),
        );
        const nbConfigurationsInDatabase = values.configurations.filter(r => !!r.uuid).length;
        confirm({
            title: (
                <FormattedMessage
                    id={'configurations.actions.delete.several'}
                    values={{
                        number: configurations.length,
                        type: <FormattedMessage id={'form.value.type.' + configurationType} />,
                    }}
                />
            ),
            content:
                selectedConfigurations.filter(r => r < nbConfigurationsInDatabase).length > 0 && dirty ? (
                    <FormattedMessage id={'configurations.alert.delete'} />
                ) : null,
            confirmationText: <FormattedMessage id={'confirm.default.delete'} />,
            cancellationText: <FormattedMessage id={'confirm.default.cancel'} />,
        }).then(() => {
            const configurationsToDelete = configurations.filter(r => !!r.uuid);
            if (configurationsToDelete.length > 0) {
                mutate({ airportConfiguration: configurationsToDelete });
            } else {
                selectedConfigurations.sort().forEach((index, i) => arrayRemove(index - i));
                setSelectedConfigurations([]);
            }
        });
    }, [
        arrayRemove,
        configurationType,
        confirm,
        dirty,
        mutate,
        selectedConfigurations,
        setAnchorEl,
        setSelectedConfigurations,
        values.configurations,
    ]);

    return (
        <StyledFormBox>
            <StyledFormButton onClick={handleDelete}>
                <FormattedMessage
                    id={`Configurations.delete.configurations`}
                    values={{ type: <FormattedMessage id={'form.value.type.' + configurationType} /> }}
                />
            </StyledFormButton>
        </StyledFormBox>
    );
});
